import React, { useMemo } from "react";

import AddToWatchlistActionsheetView from "./AddToWatchlistActionsheetView";
import { CustomPromptRefProps } from "@/components/molecules/CustomPrompt/CustomPromptContainer";

import {
  PlayerSearchPlayerFragment,
  useWatchlistListMyUserWatchlistsQuery,
} from "@/generated/graphql";
import { MY_WATCHLISTS_PAGE_SIZE } from "@/features/watchlist/constants";
import useMobileScreen from "@/hooks/useMobileScreen";
import { PlayerCardData } from "@/types/playerCardTypes";

/**
 * Player accepted by the add-to-watchlist actionsheet.
 * @remarks The actionsheet + its add/remove buttons only read identity fields
 *   (id, first/last name), so it accepts either a full search-result player or
 *   the lightweight roster/watchlist card node (PlayerCardData) — the selected
 *   roster player no longer carries the full search fragment after the SE-5377
 *   roster-list trim. `isManual` is optional since this component does not use it.
 */
type ActionsheetPlayerItem =
  | (Omit<PlayerSearchPlayerFragment, "isManual"> & {
      isManual?: boolean;
    })
  | PlayerCardData;

/**
 *
 */
interface Props {
  item: ActionsheetPlayerItem;
  showActionsheet: boolean;
  onClose: () => void;
  onAddPress: () => void;
  onAddWatchlistPress: () => void;
  onAddPlayerToWatchlistSuccess?: () => void;
  loading: boolean;
  isPlayerInAnyWatchlist: boolean;
  promptRef?: React.RefObject<CustomPromptRefProps>;
  isDark: boolean;
}

/**
 * Container component for add to watchlist actionsheet functionality
 * @param root0 - Component props
 * @param root0.item - Player data to add to watchlist
 * @param root0.showActionsheet - Whether the actionsheet is visible
 * @param root0.onClose - Callback invoked when actionsheet is closed
 * @param root0.onAddPress - Callback invoked when add button is pressed
 * @param root0.onAddWatchlistPress - Callback invoked to create a new watchlist
 * @param root0.onAddPlayerToWatchlistSuccess - Optional callback invoked after successfully adding player to watchlist
 * @param root0.loading - Whether an add operation is in progress
 * @param root0.isPlayerInAnyWatchlist - Whether player is already in any watchlist
 * @param root0.promptRef - Ref to custom prompt component for user input
 * @param root0.isDark - Whether dark theme is active
 */
const AddToWatchlistActionsheetContainer = ({
  item,
  showActionsheet,
  onClose,
  onAddPress,
  onAddWatchlistPress,
  onAddPlayerToWatchlistSuccess,
  loading,
  isPlayerInAnyWatchlist,
  promptRef,
  isDark,
}: Props) => {
  const { data: watchlistsData } = useWatchlistListMyUserWatchlistsQuery({
    variables: { first: MY_WATCHLISTS_PAGE_SIZE },
    skip: !showActionsheet,
  });

  const isSmallScreen = useMobileScreen();

  const watchlists = useMemo(
    () =>
      watchlistsData?.listMyUserWatchlists.edges.map(
        edge => edge.node.watchlist
      ) ?? [],
    [watchlistsData]
  );

  return (
    <AddToWatchlistActionsheetView
      item={item}
      showActionsheet={showActionsheet}
      onClose={onClose}
      onAddPress={onAddPress}
      watchlists={watchlists}
      onAddWatchlistPress={onAddWatchlistPress}
      loading={loading}
      isPlayerInAnyWatchlist={isPlayerInAnyWatchlist}
      onAddPlayerToWatchlistSuccess={onAddPlayerToWatchlistSuccess}
      promptRef={promptRef}
      isSmallScreen={isSmallScreen}
      isDark={isDark}
    />
  );
};

export default AddToWatchlistActionsheetContainer;
